import { Trans } from 'react-i18next';

import {
  actions,
  projectInvoiceStatusConfig,
  FullProjectInvoice,
  ProjectInvoiceDiscount,
} from 'entities';

import { Alert, Modal, Button, Column, Row, LabeledTextarea, Label } from 'components/Blocks';
import { DecimalInput } from 'components/Inputs';
import { PercentTicketIcon } from 'components/Icons';

import { DiscountRequestTypeSelect } from 'pages/Invoice/components';

import { useLang, useMultiStateV2 } from 'hooks';
import { useProjectProfile } from 'pages/Projects/ProjectProfile/context';
import { validateDiscount } from '../ProjectInvoiceCreateModal/utils';

import { getID } from 'utils/id';
import { formatCurrency } from 'utils/text';
import { emptyFn } from 'utils/function';

interface DiscountRequestModalProps {
  projectInvoice: FullProjectInvoice;
  open: boolean;
  close: () => void;
}

export function DiscountRequestModal({ projectInvoice, close, open }: DiscountRequestModalProps) {
  const { t } = useLang();

  const { updateProject } = useProjectProfile();

  const [
    { percentage, requestComment, salesDefined, type, value: discountValue },
    { dispatch, resetDefault, getSetter },
  ] = useMultiStateV2({
    percentage: '',
    requestComment: '',
    salesDefined: false,

    type: 'PERCENTAGE' as ProjectInvoiceDiscount['type'],
    value: '',
  });

  const { discount, value, fullValue, currency } = projectInvoice;

  const totalValue = fullValue ?? value;

  const currentDiscount = Number(totalValue) * (Number(discount ?? 0) / 100);

  const isPercent = type === 'PERCENTAGE';

  const newDiscountValue = isPercent
    ? Number(totalValue) * (Number(percentage ?? 0) / 100)
    : Number(discountValue) || 0;

  const newDiscountPercentage = isPercent
    ? percentage
    : (newDiscountValue / Number(totalValue)) * 100;

  const finalValue = Number(totalValue) - (newDiscountValue || currentDiscount);

  const valueIsOK = validateDiscount(
    { percentage: `${newDiscountPercentage}`, type: 'PERCENTAGE' },
    0,
  );

  const handleClose = (): void => {
    resetDefault();
    close();
  };

  const requestDiscount = actions.invoice.createProjectInvoiceDiscountRequest.use({
    params: {
      requestComment,
      project: projectInvoice.project,
      projectInvoice: projectInvoice.id,
      salesDefined,

      type,
      percentage: Number(newDiscountPercentage).toFixed(2),
      value: isPercent ? undefined : discountValue,
    },

    onSuccess: () => {
      updateProject((old) => ({
        ...old,

        invoice: {
          ...old.invoice,

          projectInvoices: old.invoice.projectInvoices.map((invoice) =>
            invoice.id !== projectInvoice.id
              ? invoice
              : { ...projectInvoice, status: projectInvoiceStatusConfig.discountRequested.code },
          ),
        },
      }));

      handleClose();
    },
  });

  const hasError = salesDefined ? false : !valueIsOK;

  return (
    <Modal
      title={t('request-discount') as string}
      width="min(90vw, 680px)"
      isOpen={open}
      close={handleClose}
      gap="6"
    >
      <DiscountRequestTypeSelect
        update={dispatch}
        type={type}
        salesDefined={salesDefined}
        addSalesDefinedOption
      />

      <Column gap="2">
        <Label>{t('value-to-request')}</Label>

        {type === 'PERCENTAGE' ? (
          <DecimalInput
            className={hasError ? 'border-destructive' : ''}
            disabled={salesDefined}
            onChangeDirect={salesDefined ? emptyFn : getSetter('percentage')}
            value={percentage}
            name={`percentage-${getID()}`}
            placeholder={`${t('discount')} %`}
          />
        ) : (
          <DecimalInput
            className={hasError ? 'border-destructive' : ''}
            disabled={salesDefined}
            onChangeDirect={salesDefined ? emptyFn : getSetter('value')}
            value={discountValue}
            name={`value-${getID()}`}
            placeholder={salesDefined ? '???????' : `${t('value')} $`}
          />
        )}
      </Column>

      <LabeledTextarea
        value={requestComment}
        label={t('why-is-this-discount-necessary')!}
        onChangeDirect={getSetter('requestComment')}
      />

      <Column>
        <Row justifyContent="space-between">
          <strong>{t('full-value')}</strong>

          <span>{formatCurrency(totalValue, currency)}</span>
        </Row>

        {!!currentDiscount && (
          <Row justifyContent="space-between">
            <span className="text-sm italic">
              {t('current-discount')} {`(${discount}%)`}
            </span>

            <span>
              <s>{formatCurrency(currentDiscount, currency)}</s>
            </span>
          </Row>
        )}

        <Row justifyContent="space-between">
          <strong>
            {t('new-discount')} {`(${salesDefined ? '???' : newDiscountPercentage}%)`}
          </strong>

          <span>{salesDefined ? '???' : formatCurrency(newDiscountValue, currency)}</span>
        </Row>

        <hr />

        <Row justifyContent="space-between">
          <strong>{t('final-value')}</strong>

          <span>{salesDefined ? '???' : formatCurrency(finalValue, currency)}</span>
        </Row>
      </Column>

      {!!currentDiscount && (
        <Alert level="info" title={t('attention')}>
          <Trans i18nKey="there-is-already-a-discount-in-effect-on-this-project-invoice-if-you-request-a-new-one-your-value-will-overwrite-the-current-amount" />
        </Alert>
      )}

      <Button
        variant="outlined"
        disabled={!requestComment.trim() || (salesDefined ? false : !valueIsOK)}
        {...requestDiscount.buttonProps}
      >
        <PercentTicketIcon />
        {t('request')}
      </Button>
    </Modal>
  );
}
